import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './HackerToggleSwitch.css';

export interface HackerToggleSwitchProps {
    mode: 'home' | 'order';
    status?: 'idle' | 'holding' | 'pending' | 'locked' | 'error';
    onModeChange: (mode: 'home' | 'order') => void;
    visible?: boolean;
    holdDuration?: number; // milliseconds, default 1000
}

const HackerToggleSwitch: React.FC<HackerToggleSwitchProps> = ({
    mode,
    status = 'idle',
    onModeChange,
    visible = true,
    holdDuration = 500, // Reduced from 1000ms for faster switching
}) => {
    const [isHolding, setIsHolding] = useState(false);
    const [holdProgress, setHoldProgress] = useState(0);
    const [glowIntensity, setGlowIntensity] = useState(0.3);
    const holdTimerRef = useRef<number | null>(null);
    const progressIntervalRef = useRef<number | null>(null);



    // Determine glow color based on mode and status (matches app theme)
    const getGlowColor = () => {
        if (status === 'error') return '#ff4757'; // neon red
        if (status === 'pending') return '#00d4ff'; // neon blue
        if (mode === 'order') return '#00d4ff'; // neon blue
        return '#00ff88'; // neon green (app primary)
    };

    // Check if device is mobile (screen width < 768px)
    const isMobile = () => window.matchMedia('(max-width: 768px)').matches;

    // Handle pointer down - instant toggle on mobile, hold on desktop
    const handlePointerDown = (e: React.PointerEvent) => {
        if (status === 'locked' || status === 'pending') return;

        e.preventDefault();

        // On mobile: quick animated toggle (show progress bar briefly)
        if (isMobile()) {
            // Capture target mode NOW before any async operations
            const targetMode = mode === 'home' ? 'order' : 'home';

            setIsHolding(true);
            setHoldProgress(0);

            // Haptic feedback
            if ('vibrate' in navigator) {
                navigator.vibrate(50);
            }

            // Quick progress animation (200ms)
            const mobileAnimDuration = 200;
            const startTime = Date.now();
            progressIntervalRef.current = window.setInterval(() => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / mobileAnimDuration, 1);
                setHoldProgress(progress);
                setGlowIntensity(0.3 + progress * 0.7);
            }, 16);

            // Complete after animation
            holdTimerRef.current = window.setTimeout(() => {
                if (progressIntervalRef.current) {
                    clearInterval(progressIntervalRef.current);
                    progressIntervalRef.current = null;
                }
                setIsHolding(false);
                setHoldProgress(0);

                // Use the captured target mode
                onModeChange(targetMode);

                // Haptic feedback
                if ('vibrate' in navigator) {
                    navigator.vibrate([30, 50, 30]);
                }
            }, mobileAnimDuration);
            return;
        }

        // On desktop: hold to toggle behavior
        setIsHolding(true);
        setHoldProgress(0);

        // Haptic feedback if available
        if ('vibrate' in navigator) {
            navigator.vibrate(50);
        }

        // Start progress animation
        const startTime = Date.now();
        progressIntervalRef.current = window.setInterval(() => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / holdDuration, 1);
            setHoldProgress(progress);
            setGlowIntensity(0.3 + progress * 0.7); // Ramp from 0.3 to 1.0
        }, 16); // ~60fps

        // Set timer for hold completion
        holdTimerRef.current = window.setTimeout(() => {
            handleHoldComplete();
        }, holdDuration);
    };

    // Handle pointer up - cancel if not complete
    const handlePointerUp = () => {
        if (!isHolding) return;

        // Clear timers
        if (holdTimerRef.current) {
            clearTimeout(holdTimerRef.current);
            holdTimerRef.current = null;
        }
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
        }

        // If not complete, reset
        if (holdProgress < 1) {
            setIsHolding(false);
            setHoldProgress(0);
            setGlowIntensity(0.3);
        }
    };

    // Handle hold completion
    const handleHoldComplete = () => {
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
        }

        setIsHolding(false);
        setHoldProgress(0);

        // Trigger mode change
        const newMode = mode === 'home' ? 'order' : 'home';
        onModeChange(newMode);

        // Haptic feedback
        if ('vibrate' in navigator) {
            navigator.vibrate([30, 50, 30]);
        }
    };

    // Handle keyboard events for accessibility
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (status === 'locked' || status === 'pending') return;

        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            // Trigger immediate mode change on key press
            const newMode = mode === 'home' ? 'order' : 'home';
            onModeChange(newMode);

            // Haptic feedback
            if ('vibrate' in navigator) {
                navigator.vibrate([30, 50, 30]);
            }
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        };
    }, []);

    // Update glow intensity based on mode when not holding
    useEffect(() => {
        if (!isHolding) {
            setGlowIntensity(mode === 'order' ? 0.6 : 0.3);
        }
    }, [mode, isHolding]);

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    className="hacker-toggle-root"
                    initial={{ opacity: 0, scale: 0.95, y: -20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -20 }}
                    transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
                >
                    <div className={`switch-container ${status}`}>
                        <svg
                            viewBox="60 20 800 140"
                            className="switch-svg"
                            onPointerDown={handlePointerDown}
                            onPointerUp={handlePointerUp}
                            onPointerLeave={handlePointerUp}
                            onClick={(e) => {
                                // Fallback for mobile click - trigger if on mobile and not already holding
                                if (isMobile() && !isHolding && status !== 'pending' && status !== 'locked') {
                                    e.preventDefault();
                                    handlePointerDown(e as unknown as React.PointerEvent);
                                }
                            }}
                            onKeyDown={handleKeyDown}
                            style={{ touchAction: 'manipulation' }}
                            preserveAspectRatio="xMidYMid meet"
                            role="switch"
                            aria-checked={mode === 'order'}
                            aria-label={`Navigation toggle, currently on ${mode.toUpperCase()}. Tap or press Enter to switch.`}
                            tabIndex={status === 'locked' ? -1 : 0}
                        >
                            {/* SVG Filters for Glow */}
                            <defs>
                                {/* Soft glow */}
                                <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                                    <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                                    <feMerge>
                                        <feMergeNode in="coloredBlur" />
                                        <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                </filter>

                                {/* Strong glow for active elements */}
                                <filter id="strong-glow" x="-100%" y="-100%" width="300%" height="300%">
                                    <feGaussianBlur stdDeviation={4 * glowIntensity} result="blur1" />
                                    <feGaussianBlur stdDeviation={8 * glowIntensity} result="blur2" />
                                    <feMerge>
                                        <feMergeNode in="blur2" />
                                        <feMergeNode in="blur1" />
                                        <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                </filter>

                                {/* Gradients - App Theme Colors */}
                                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#00ff88" stopOpacity="0.8" />
                                    <stop offset="100%" stopColor="#00d4ff" stopOpacity="0.8" />
                                </linearGradient>

                                <linearGradient id="frameGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#00ff88" stopOpacity="0.3" />
                                    <stop offset="50%" stopColor="#0a0a0f" stopOpacity="0.1" />
                                    <stop offset="100%" stopColor="#00d4ff" stopOpacity="0.3" />
                                </linearGradient>
                            </defs>

                            {/* Main futuristic frame - outer shell */}
                            <path
                                d="M 80 50 L 120 30 L 800 30 L 840 50 L 840 130 L 800 150 L 120 150 L 80 130 Z"
                                fill="rgba(5, 10, 20, 0.4)"
                                stroke="url(#frameGradient)"
                                strokeWidth="1.5"
                                filter="url(#glow)"
                            />

                            {/* Circuit detail lines - left side (neon green) */}
                            <line x1="100" y1="40" x2="250" y2="40" stroke="rgba(0, 255, 136, 0.2)" strokeWidth="1" />
                            <line x1="100" y1="140" x2="250" y2="140" stroke="rgba(0, 255, 136, 0.2)" strokeWidth="1" />
                            <line x1="120" y1="50" x2="120" y2="70" stroke="rgba(0, 255, 136, 0.15)" strokeWidth="0.5" />
                            <line x1="150" y1="50" x2="150" y2="70" stroke="rgba(0, 255, 136, 0.15)" strokeWidth="0.5" />

                            {/* Circuit detail lines - right side (neon blue) */}
                            <line x1="670" y1="40" x2="820" y2="40" stroke="rgba(0, 212, 255, 0.2)" strokeWidth="1" />
                            <line x1="670" y1="140" x2="820" y2="140" stroke="rgba(0, 212, 255, 0.2)" strokeWidth="1" />
                            <line x1="800" y1="50" x2="800" y2="70" stroke="rgba(0, 212, 255, 0.15)" strokeWidth="0.5" />
                            <line x1="770" y1="50" x2="770" y2="70" stroke="rgba(0, 212, 255, 0.15)" strokeWidth="0.5" />

                            {/* HOME label - left side (neon green) */}
                            <text
                                x="180"
                                y="95"
                                fontSize="42"
                                fontFamily="'Orbitron', 'Rajdhani', 'Roboto Mono', monospace"
                                fontWeight="700"
                                letterSpacing="4"
                                fill={mode === 'home' ? '#00ff88' : 'rgba(148, 163, 184, 0.4)'}
                                filter={mode === 'home' ? 'url(#strong-glow)' : 'none'}
                                style={{ transition: 'fill 0.3s ease' }}
                            >
                                HOME
                            </text>

                            {/* ORDER label - right side (neon blue) */}
                            <text
                                x="630"
                                y="95"
                                fontSize="42"
                                fontFamily="'Orbitron', 'Rajdhani', 'Roboto Mono', monospace"
                                fontWeight="700"
                                letterSpacing="4"
                                fill={mode === 'order' ? '#00d4ff' : 'rgba(148, 163, 184, 0.4)'}
                                filter={mode === 'order' ? 'url(#strong-glow)' : 'none'}
                                style={{ transition: 'fill 0.3s ease' }}
                            >
                                ORDER
                            </text>

                            {/* Micro-text details - left side (neon green) */}
                            <text x="110" y="125" fontSize="8" fill="rgba(0, 255, 136, 0.4)" fontFamily="'Roboto Mono', monospace" letterSpacing="0.5">
                                IDLE
                            </text>
                            <text x="110" y="60" fontSize="8" fill="rgba(0, 255, 136, 0.3)" fontFamily="'Roboto Mono', monospace" letterSpacing="0.5">
                                NODE_01
                            </text>

                            {/* Micro-text details - right side (neon blue) */}
                            <text x="780" y="125" fontSize="8" fill="rgba(0, 212, 255, 0.4)" fontFamily="'Roboto Mono', monospace" letterSpacing="0.5">
                                FREE
                            </text>
                            <text x="780" y="60" fontSize="8" fill="rgba(0, 212, 255, 0.3)" fontFamily="'Roboto Mono', monospace" letterSpacing="0.5">
                                LIVE
                            </text>

                            {/* Central track/rail */}
                            <rect
                                x="360"
                                y="82"
                                width="200"
                                height="16"
                                rx="8"
                                fill="rgba(15, 23, 42, 0.6)"
                                stroke="rgba(71, 85, 105, 0.3)"
                                strokeWidth="1"
                            />

                            {/* Progress bar during hold */}
                            {isHolding && (
                                <motion.rect
                                    x="360"
                                    y="88"
                                    width={200 * holdProgress}
                                    height="4"
                                    rx="2"
                                    fill="url(#progressGradient)"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    filter="url(#glow)"
                                />
                            )}

                            {/* Central sliding core mechanism */}
                            <motion.g
                                animate={{ x: mode === 'home' ? 380 : 520 }}
                                transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
                            >
                                {/* Outer glow ring */}
                                <ellipse
                                    cx="0"
                                    cy="90"
                                    rx={24 + holdProgress * 8}
                                    ry={20 + holdProgress * 6}
                                    fill={getGlowColor()}
                                    opacity={0.15 * glowIntensity}
                                    filter="url(#strong-glow)"
                                />

                                {/* Main slider body */}
                                <rect
                                    x="-18"
                                    y="74"
                                    width="36"
                                    height="32"
                                    rx="6"
                                    fill="rgba(15, 23, 42, 0.9)"
                                    stroke={getGlowColor()}
                                    strokeWidth="2"
                                    filter="url(#strong-glow)"
                                    opacity={glowIntensity}
                                />

                                {/* Inner indicator bars with idle pulse */}
                                <motion.rect
                                    x="-12"
                                    y="82"
                                    width="8"
                                    height="16"
                                    rx="2"
                                    fill={getGlowColor()}
                                    animate={!isHolding ? { opacity: [0.5, 0.8, 0.5] } : { opacity: 0.8 }}
                                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                />
                                <motion.rect
                                    x="4"
                                    y="82"
                                    width="8"
                                    height="16"
                                    rx="2"
                                    fill={getGlowColor()}
                                    animate={!isHolding ? { opacity: [0.5, 0.8, 0.5] } : { opacity: 0.8 }}
                                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                                />

                                {/* Center status indicator */}
                                <circle
                                    cx="0"
                                    cy="90"
                                    r="3"
                                    fill={status === 'pending' ? '#22d3ee' : status === 'error' ? '#ff4757' : '#fff'}
                                    filter="url(#glow)"
                                    className={status === 'pending' ? 'pulse-animation' : ''}
                                />
                            </motion.g>

                            {/* Corner accent details */}
                            <circle cx="95" cy="45" r="2" fill="rgba(34, 211, 238, 0.4)" filter="url(#glow)" />
                            <circle cx="95" cy="135" r="2" fill="rgba(34, 211, 238, 0.4)" filter="url(#glow)" />
                            <circle cx="825" cy="45" r="2" fill="rgba(251, 191, 36, 0.4)" filter="url(#glow)" />
                            <circle cx="825" cy="135" r="2" fill="rgba(251, 191, 36, 0.4)" filter="url(#glow)" />
                        </svg>

                        {/* Status text below */}
                        <div className="status-text">
                            {status === 'pending' && 'SYNCHRONIZING'}
                            {status === 'locked' && 'SYSTEM LOCKED'}
                            {status === 'error' && 'ERROR DETECTED'}
                            {isHolding && `ARMING ${Math.round(holdProgress * 100)}%`}
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default HackerToggleSwitch;
